import { userActions } from '@/actions';
import { EVENT_NAME, SCREEN_NAME } from '@/constants/analytics';
import { NAVIGATION_TRIGGER } from '@/constants/navigation';
import { trackMpEvent, url } from '@/helpers';
import { useAppDispatch } from '@/hooks';
import { createContext, MouseEvent, ReactNode, useContext, useState } from 'react';
import { AccountNavItemIdentifier } from './AccountNavigation.types';

export type AccountNavigationContextType = ReturnType<typeof useAccountNavigationManager>;

const useAccountNavigationManager = () => {
  const [show, setShow] = useState(false);
  const [showConfirmLogoutModal, setShowConfirmLogoutModal] = useState(false);
  const dispatch = useAppDispatch();

  function handleOnClose() {
    setShow(false);
  }
  function handleOnOpen() {
    setShow(true);
  }

  function handleOnAbortLogout() {
    trackMpEvent(EVENT_NAME.LOGOUT_CANCELED, {
      screen_name: url.getScreenNameFromUrl(window.location.href),
      initiated_from: NAVIGATION_TRIGGER.NAV_DRAWER,
    });
    setShowConfirmLogoutModal(false);
  }

  function handleOnLogoutConfirm() {
    trackMpEvent(EVENT_NAME.LOGOUT_LOGGED_OUT, {
      screen_name: SCREEN_NAME.HOME,
      initiated_from: NAVIGATION_TRIGGER.NAV_DRAWER,
    });
    handleOnClose();
    dispatch(userActions.logout());
  }

  function handleOnLogoutClick(event: MouseEvent<HTMLButtonElement>) {
    event.currentTarget.blur();
    setShowConfirmLogoutModal(true);
  }

  function handleOnNavigationItemClick(identifier: AccountNavItemIdentifier) {
    switch (identifier) {
      default:
        handleOnClose();
    }
  }

  return {
    show,
    onClose: handleOnClose,
    onOpen: handleOnOpen,
    showConfirmLogoutModal,
    onAbortLogout: handleOnAbortLogout,
    onLogoutClick: handleOnLogoutClick,
    onLogoutConfirm: handleOnLogoutConfirm,
    onNavigationItemClick: handleOnNavigationItemClick,
  };
};

export const AccountNavigationContext = createContext<AccountNavigationContextType>({
  show: false,
  onClose: () => {},
  onOpen: () => {},
  showConfirmLogoutModal: false,
  onAbortLogout: () => {},
  onLogoutClick: () => {},
  onLogoutConfirm: () => {},
  onNavigationItemClick: () => {},
});

export const useAccountNavigationContext = () => {
  const context = useContext(AccountNavigationContext);

  if (!context) {
    throw new Error('useAccountNavigationContext must be used within a AccountNavigationProvider');
  }

  return context;
};

export const AccountNavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <AccountNavigationContext.Provider value={useAccountNavigationManager()}>
      {children}
    </AccountNavigationContext.Provider>
  );
};
